import { makeAutoObservable, runInAction } from 'mobx';

interface MediaStreamOptions {
  video?: boolean | MediaTrackConstraints;
  audio?: boolean | MediaTrackConstraints;
  selectedMicrophone?: string;
  selectedCamera?: string;
}

class MediaStreamStore {
  private stream: MediaStream | null = null;
  private referenceCount = 0;
  private streamOptions: MediaStreamOptions | null = null;
  private trackedStreams = new Set<MediaStream>();
  private getUserMediaPatched = false;

  constructor() {
    makeAutoObservable(this);
    this.patchGetUserMedia();
  }

  /**
   * Перехватываем navigator.mediaDevices.getUserMedia, чтобы знать обо ВСЕХ
   * созданных в приложении стримах (а не только о тех, что прошли через стор).
   * Это позволит позже их принудительно остановить из stopStream.
   */
  private patchGetUserMedia(): void {
    if (this.getUserMediaPatched) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return;
    }
    const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    navigator.mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
      const stream = await original(constraints);
      this.trackedStreams.add(stream);
      return stream;
    };
    this.getUserMediaPatched = true;
  }

  /**
   * Получить текущий стрим или создать новый
   */
  async getStream(options?: MediaStreamOptions): Promise<MediaStream> {
    // Если стрим уже существует, проверяем, живы ли треки
    if (this.stream && !this.isStreamAlive()) {
      this.stopStream();
    }

    // Если стрим уже существует и параметры совпадают, возвращаем его
    if (this.stream && this.isStreamCompatible(options)) {
      this.referenceCount++;
      return this.stream;
    }

    // Если стрим существует, но параметры не совместимы, останавливаем его
    if (this.stream) {
      this.stopStream();
    }

    // Создаем новый стрим
    const newStream = await this.createStream(options);
    
    runInAction(() => {
      this.stream = newStream;
      this.streamOptions = options || null;
      this.referenceCount = 1;
    });

    return newStream;
  }

  /**
   * Получить текущий стрим без создания нового
   */
  getCurrentStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Увеличить счетчик ссылок (когда хук начинает использовать стрим)
   */
  addReference(): void {
    if (this.stream) {
      this.referenceCount++;
    }
  }

  /**
   * Уменьшить счетчик ссылок (когда хук перестает использовать стрим)
   */
  removeReference(): void {
    if (this.stream && this.referenceCount > 0) {
      this.referenceCount--;
      
      // Если больше никто не использует стрим, останавливаем его
      if (this.referenceCount === 0) {
        this.stopStream();
      }
    }
  }

  /**
   * Принудительно остановить стрим и все треки во всех возможных местах:
   * 1) основной стрим стора
   * 2) все стримы, когда-либо созданные через getUserMedia (отслеживаем через перехват)
   * 3) srcObject у всех <video>/<audio> элементов в DOM
   *
   * Можно безопасно вызывать на страницах, где доступ к камере/микрофону не нужен.
   */
  stopStream(): void {
    // 1. Основной стрим стора
    if (this.stream) {
      this.forceStopStream(this.stream);
    }

    // 2. Все стримы, созданные через getUserMedia
    this.trackedStreams.forEach((stream) => {
      this.forceStopStream(stream);
    });
    this.trackedStreams.clear();

    // 3. Все <video>/<audio> элементы — отвязываем srcObject и стопим их треки
    if (typeof document !== 'undefined') {
      const elements = document.querySelectorAll<HTMLMediaElement>('video, audio');
      elements.forEach((el) => {
        const so = el.srcObject;
        if (so instanceof MediaStream) {
          this.forceStopStream(so);
        }
        try {
          el.pause();
          el.srcObject = null;
          el.removeAttribute('src');
          el.load();
        } catch {
          // элемент мог быть удалён из DOM прямо сейчас — игнорируем
        }
      });
    }

    runInAction(() => {
      this.stream = null;
      this.streamOptions = null;
      this.referenceCount = 0;
    });
  }

  /**
   * Принудительно остановить и отсоединить все треки конкретного стрима.
   * Каждый шаг обёрнут в try/catch — браузеры по-разному реагируют
   * на повторный stop() уже остановленного трека.
   */
  private forceStopStream(stream: MediaStream): void {
    try {
      const tracks = stream.getTracks();
      tracks.forEach((track) => {
        try {
          track.enabled = false;
        } catch {
          // ignore
        }
        try {
          track.stop();
        } catch {
          // ignore
        }
        try {
          stream.removeTrack(track);
        } catch {
          // ignore
        }
      });
    } catch {
      // ignore
    }
  }

  /**
   * Проверить, живы ли треки стрима (не перешли в состояние "ended")
   */
  private isStreamAlive(): boolean {
    if (!this.stream) return false;
    const tracks = this.stream.getTracks();
    if (tracks.length === 0) return false;
    return tracks.every((track) => track.readyState === 'live');
  }

  /**
   * Проверить, совместим ли текущий стрим с запрошенными параметрами
   */
  private isStreamCompatible(options?: MediaStreamOptions): boolean {
    if (!options || !this.streamOptions || !this.stream) {
      return true; // Если параметры не указаны или стрим отсутствует, считаем совместимым
    }

    // Проверяем наличие нужных треков
    const hasVideo = options.video !== false && this.stream.getVideoTracks().length > 0;
    const hasAudio = options.audio !== false && this.stream.getAudioTracks().length > 0;

    // Проверяем соответствие устройств
    if (options.selectedCamera) {
      const videoTrack = this.stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        if (settings.deviceId !== options.selectedCamera) {
          return false;
        }
      }
    }

    if (options.selectedMicrophone) {
      const audioTrack = this.stream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        if (settings.deviceId !== options.selectedMicrophone) {
          return false;
        }
      }
    }

    return hasVideo && hasAudio;
  }

  /**
   * Создать новый медиа-стрим с fallback стратегиями
   */
  private async createStream(options?: MediaStreamOptions): Promise<MediaStream> {
    const constraintsVariants: MediaStreamConstraints[] = [];

    // Стратегия 1: С указанными устройствами и оптимальными настройками
    if (options?.selectedCamera || options?.selectedMicrophone) {
      constraintsVariants.push({
        video: options.selectedCamera
          ? { width: 1280, height: 720, deviceId: { exact: options.selectedCamera } }
          : options.video !== false ? { width: 1280, height: 720 } : false,
        audio: options.selectedMicrophone
          ? {
              deviceId: { exact: options.selectedMicrophone },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          : options.audio !== false
          ? {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          : false
      });
    }

    // Стратегия 2: С указанными устройствами, но базовыми настройками
    if (options?.selectedCamera || options?.selectedMicrophone) {
      constraintsVariants.push({
        video: options.selectedCamera
          ? { width: 1280, height: 720, deviceId: { exact: options.selectedCamera } }
          : options.video !== false ? { width: 1280, height: 720 } : false,
        audio: options.selectedMicrophone
          ? { deviceId: { exact: options.selectedMicrophone } }
          : options.audio !== false
      });
    }

    // Стратегия 3: Без конкретных устройств, но с настройками
    constraintsVariants.push({
      video: options?.video !== false ? { width: 1280, height: 720 } : false,
      audio: options?.audio !== false ? true : false
    });

    // Стратегия 4: Базовые настройки
    constraintsVariants.push({
      video: options?.video !== false,
      audio: options?.audio !== false
    });

    // Пробуем каждую стратегию
    let lastError: unknown = null;
    for (const constraints of constraintsVariants) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return stream;
      } catch (e) {
        lastError = e;
        if ((e as DOMException)?.name === 'NotReadableError') {
          continue;
        }
        continue;
      }
    }

    // Если не удалось получить поток
    const errorName = (lastError as DOMException)?.name;
    const errorMessage =
      errorName === 'NotReadableError'
        ? 'Аудио или видео устройство занято другой программой. Закройте приложения, использующие микрофон или камеру (Zoom/Meet/Skype/OBS) и попробуйте снова.'
        : errorName === 'NotFoundError'
        ? 'Не найдено указанное аудио или видео устройство. Проверьте настройки устройств.'
        : errorName === 'NotAllowedError'
        ? 'Доступ к микрофону или камере запрещен. Разрешите доступ в настройках браузера.'
        : lastError instanceof Error
        ? lastError.message
        : 'Не удалось получить доступ к микрофону или камере';

    throw new Error(errorMessage);
  }

  /**
   * Получить только аудио стрим (для анализа голоса)
   */
  async getAudioStream(selectedMicrophone?: string): Promise<MediaStream> {
    // Если текущий стрим содержит аудио и совместим, используем его
    if (this.stream && this.isStreamAlive() && this.stream.getAudioTracks().length > 0) {
      if (!selectedMicrophone) {
        this.referenceCount++;
        return this.stream;
      }
      
      const audioTrack = this.stream.getAudioTracks()[0];
      const settings = audioTrack.getSettings();
      if (settings.deviceId === selectedMicrophone) {
        this.referenceCount++;
        return this.stream;
      }
    }

    // Создаем новый аудио стрим
    const audioConstraintsVariants: MediaStreamConstraints[] = [
      { audio: true },
      {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      }
    ];

    if (selectedMicrophone) {
      audioConstraintsVariants.push({
        audio: {
          deviceId: { exact: selectedMicrophone },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
    }

    let audioStream: MediaStream | null = null;
    let lastError: unknown = null;
    for (const constraints of audioConstraintsVariants) {
      try {
        audioStream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (e) {
        lastError = e;
        continue;
      }
    }

    if (!audioStream) {
      const message =
        (lastError as DOMException)?.name === 'NotReadableError'
          ? 'Микрофон занят другой программой. Закройте приложения, использующие микрофон (Zoom/Meet/Skype/OBS) и попробуйте снова.'
          : lastError instanceof Error
          ? lastError.message
          : 'Не удалось получить доступ к микрофону';
      throw new Error(message);
    }

    return audioStream;
  }
}

export const mediaStreamStore = new MediaStreamStore();

